import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronDown, faEnvelope, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { Badge } from '../shared/components/ui'
import DeviceAccessPanel from './DeviceAccessPanel'
import type { GroupedGuestUser, GuestAccessStatus } from '../types/guest'

interface GuestUserRowProps {
  user: GroupedGuestUser
  onToggleDevice: (guestEmail: string, deviceId: number, grant: boolean) => Promise<void>
  onRevokeAll: (guestEmail: string) => void
  onResendInvite: (guestEmail: string) => void
  onDeleteRevoked: (guestEmail: string) => void
}

export default function GuestUserRow({
  user,
  onToggleDevice,
  onRevokeAll,
  onResendInvite,
  onDeleteRevoked,
}: GuestUserRowProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const getStatusBadge = (status: GuestAccessStatus) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" size="sm">Active</Badge>
      case 'pending':
        return <Badge variant="warning" size="sm">Pending</Badge>
      case 'revoked':
        return <Badge variant="error" size="sm">Revoked</Badge>
      default:
        return <Badge variant="default" size="sm">{status}</Badge>
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setIsExpanded(!isExpanded)
    }
  }

  const handleToggleDevice = async (deviceId: number, grant: boolean) => {
    await onToggleDevice(user.email, deviceId, grant)
  }

  return (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {/* Header - Clickable */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={handleKeyDown}
        role="button"
        aria-expanded={isExpanded}
        aria-label={`${user.email}, ${user.status}, ${user.accessibleDevices} devices`}
        tabIndex={0}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {/* Chevron */}
          <FontAwesomeIcon
            icon={isExpanded ? faChevronDown : faChevronRight}
            className="text-gray-400 h-4 w-4 flex-shrink-0 transition-transform"
          />

          {/* Email */}
          <span className="font-semibold text-gray-900 truncate">{user.email}</span>

          {/* Status */}
          {getStatusBadge(user.status)}

          {/* Device Count */}
          <span className="text-sm text-gray-600 flex-shrink-0">
            {user.accessibleDevices} device{user.accessibleDevices !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Date and Actions */}
        <div className="flex items-center space-x-4 flex-shrink-0 ml-4">
          <span className="text-xs text-gray-500 hidden sm:block">
            {user.status === 'active' && user.registeredDate
              ? `Registered: ${formatDate(user.registeredDate)}`
              : `Invited: ${formatDate(user.invitedDate)}`}
          </span>

          {/* Actions */}
          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
            {user.status === 'pending' && (
              <button
                onClick={() => onResendInvite(user.email)}
                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                title="Resend invitation"
                aria-label={`Resend invitation to ${user.email}`}
              >
                <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4" />
              </button>
            )}

            {user.status === 'revoked' ? (
              <button
                onClick={() => onDeleteRevoked(user.email)}
                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                title="Delete revoked user from list"
                aria-label={`Delete ${user.email} from list`}
              >
                <FontAwesomeIcon icon={faTrashAlt} className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => onRevokeAll(user.email)}
                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                title="Revoke all access"
                aria-label={`Revoke all access for ${user.email}`}
              >
                <FontAwesomeIcon icon={faTrashAlt} className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Panel */}
      {isExpanded && (
        <DeviceAccessPanel
          devices={user.devices}
          userStatus={user.status}
          onToggleDevice={handleToggleDevice}
        />
      )}
    </div>
  )
}
